import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import useNavigateByIdentificationStatus from 'hooks/useBankIdAccountSelection';
import {
  useBankIDAccountConfirmMutation,
  useBankIDSessionsQuery,
  useBankIDSessionsRejectMutation,
} from 'hooks/webapi';
import {
  BANK_ID_ERROR,
  MANUAL_PSD2_BANKS,
  WAITING,
  WELCOME_BACK,
} from 'Router/routes';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import SelectEvent from 'hooks/context/EventCollector/events/SelectEvent';
import useClientIdentificationStatusQuery from 'hooks/webapi/client/useIdentificationStatusQuery';

export default function useBankIdAccountSelection() {
  // TODO: Handle case where localStorage bankIdSessionId is not set
  const sessionId = localStorage.getItem('bankIdSessionId');
  const navigate = useNavigate();
  const { track } = useEventCollector();
  const [selectedAccount, setSelectedAccount] = useState('');

  const {
    data: bankIdSessionData,
    isLoading: bankIdSessionIsLoading,
    isFetched: isFetchedBankIdSessionData,
  } = useBankIDSessionsQuery({ uuid: sessionId! }, Boolean(sessionId));
  const { mutateAsync: rejectBankID, isPending: rejecdBankIdIsPending } =
    useBankIDSessionsRejectMutation();
  const {
    mutateAsync: confirmBankIDAccount,
    isPending: confirmBankIdIsPending,
  } = useBankIDAccountConfirmMutation();

  const { data: identificationStatus } = useClientIdentificationStatusQuery();
  const { navigateByIdentificationStatus } =
    useNavigateByIdentificationStatus('bankId');

  const [accountList, setAccountList] = useState<string[]>([]);

  const toggleAccounts = () => {
    if (accountList.length > 1) {
      const newList = [];
      newList.push(accountList[0]);
      setAccountList(newList);
      setSelectedAccount(accountList[0]);
    } else if (bankIdSessionData?.paymentAccounts) {
      setAccountList([bankIdSessionData?.paymentAccounts[0]]);
    }
  };

  const handleSelectAccount = (account: string) => {
    setSelectedAccount(account);
  };

  const confirmBankIdAccountNumber = async () => {
    // If sessionId is not loaded, we can't proceed
    if (!sessionId) {
      navigate(BANK_ID_ERROR, { state: { error: 'session_id_not_loaded' } });
      return;
    }

    if (!selectedAccount) {
      return;
    }

    await confirmBankIDAccount({
      uuid: sessionId,
      bankAccountNumber: selectedAccount,
    });

    if (
      identificationStatus?.bankAccountStatus === 'PSD2_PAYMENT_SENT' ||
      identificationStatus?.bankAccountStatus ===
        'PSD2_PAYMENT_SENT_WITH_INCOME'
    ) {
      navigate(WELCOME_BACK);
      return;
    }

    if (identificationStatus?.bankAccountStatus === 'APPROVED_WITH_INCOME') {
      navigate(WAITING);
      return;
    }

    navigateByIdentificationStatus();
  };

  const rejectBankIdAccountNumber = async () => {
    // If sessionId is not loaded, we can't proceed
    if (!sessionId) {
      navigate(BANK_ID_ERROR, { state: { error: 'session_id_not_loaded' } });
      return;
    }

    await rejectBankID(sessionId);

    navigate(MANUAL_PSD2_BANKS);
  };

  // Only one account available
  useEffect(() => {
    if (
      bankIdSessionData?.paymentAccounts &&
      bankIdSessionData.paymentAccounts.length === 1
    ) {
      setSelectedAccount(bankIdSessionData.paymentAccounts[0]);
    }
  }, [bankIdSessionData?.paymentAccounts]);

  // Track selected account
  useEffect(() => {
    if (selectedAccount === '') {
      return;
    }

    track(
      new SelectEvent({
        value: selectedAccount,
        description: 'selectedAccount',
      }),
    );
  }, [selectedAccount, track]);

  useEffect(() => {
    if (bankIdSessionData?.paymentAccounts) {
      setAccountList(bankIdSessionData?.paymentAccounts);
    }
  }, [bankIdSessionData?.paymentAccounts]);

  return {
    isPending: rejecdBankIdIsPending || confirmBankIdIsPending,
    isLoading: bankIdSessionIsLoading,
    isFetchedAccountList: isFetchedBankIdSessionData,
    accountList,
    selectedAccount,
    handleSelectAccount,
    confirmBankIdAccountNumber,
    rejectBankIdAccountNumber,
    toggleAccounts,
  };
}
